import { z } from 'zod'
import type { ApiClient } from '../apiClient'
import { migratePin } from '~/pin/migratePin'
import { pinV1Schema } from '~/pin/PinV1'
import type { PinV1 } from '~/pin/PinV1'

export const caseCollectionSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  name: z
    .string()
    .nullable()
    .transform((v) => v ?? ''),
  is_dashboard: z.boolean().nullable(),
  max_order: z.number().int(),
  is_shared: z.boolean(),
})
export type CaseCollection = z.infer<typeof caseCollectionSchema>

export const vCollectionSchema = caseCollectionSchema.extend({
  share_count: z.number().int(),
  owner_email: z.string().nullable(),
})
export type VCollection = z.infer<typeof vCollectionSchema>

export const graphCollectionRowSchema = z.object({
  id: z.string(),
  collection_id: z.string(),
  state: z.unknown(),
  order_index: z.number().int().nullable(),
  custom_notes: z
    .string()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
})
export type GraphCollectionRow = z.infer<typeof graphCollectionRowSchema>

export async function listOwnedCollections(client: ApiClient): Promise<CaseCollection[]> {
  // Order by name pending data-migration overhaul (no created_at column on legacy schema; see migration 0024 README row).
  const raw = await client.get<unknown>('/case_collection', { order: 'name.asc' })
  return z.array(caseCollectionSchema).parse(raw)
}

export async function listCollectionsView(client: ApiClient): Promise<VCollection[]> {
  const raw = await client.get<unknown>('/v_collections', { order: 'name.asc' })
  return z.array(vCollectionSchema).parse(raw)
}

export type CollectionPinResult =
  | { row: GraphCollectionRow; pin: PinV1 }
  | { row: GraphCollectionRow; error: string }

export async function listCollectionPins(
  client: ApiClient,
  collectionId: string,
): Promise<CollectionPinResult[]> {
  const raw = await client.get<unknown>('/graph_collection', {
    collection_id: `eq.${collectionId}`,
    order: 'order_index.asc.nullslast',
  })
  const rows = z.array(graphCollectionRowSchema).parse(raw)
  return rows.map((r): CollectionPinResult => {
    try {
      return { row: r, pin: migratePin(r.state) }
    } catch (err) {
      return { row: r, error: err instanceof Error ? err.message : String(err) }
    }
  })
}

export async function pinToCollection(
  client: ApiClient,
  userId: string,
  collectionId: string,
  pin: PinV1,
): Promise<void> {
  const validated = pinV1Schema.parse(pin)
  await client.post('/graph_collection', {
    user_id: userId,
    collection_id: collectionId,
    state: validated,
  })
}

export async function unpinFromCollection(client: ApiClient, pinId: string): Promise<void> {
  await client.delete('/graph_collection', { id: `eq.${pinId}` })
}

export async function renameCollection(
  client: ApiClient,
  collectionId: string,
  name: string,
): Promise<CaseCollection> {
  const updated = await client.patch<CaseCollection[]>(
    `/case_collection?id=eq.${encodeURIComponent(collectionId)}`,
    { name },
    { headers: { Prefer: 'return=representation' } },
  )
  if (updated.length === 0) throw new Error('renameCollection: no rows affected (RLS or stale id)')
  return caseCollectionSchema.parse(updated[0])
}

export async function toggleDashboard(
  client: ApiClient,
  collectionId: string,
  isDashboard: boolean,
): Promise<CaseCollection> {
  const updated = await client.patch<CaseCollection[]>(
    `/case_collection?id=eq.${encodeURIComponent(collectionId)}`,
    { is_dashboard: isDashboard },
    { headers: { Prefer: 'return=representation' } },
  )
  if (updated.length === 0) throw new Error('toggleDashboard: no rows affected (RLS or stale id)')
  return caseCollectionSchema.parse(updated[0])
}

export async function deleteCollection(client: ApiClient, collectionId: string): Promise<void> {
  await client.delete('/case_collection', { id: `eq.${collectionId}` })
}

export async function editPin(
  client: ApiClient,
  pinId: string,
  pin: PinV1,
  customNotes: string | null,
): Promise<void> {
  const validated = pinV1Schema.parse(pin)
  await client.patch(`/graph_collection?id=eq.${encodeURIComponent(pinId)}`, {
    state: validated,
    custom_notes: customNotes,
  })
}

export async function movePinUp(client: ApiClient, graphId: string): Promise<void> {
  await client.rpc('move_pin_up', { p_graph_id: graphId })
}

export async function movePinDown(client: ApiClient, graphId: string): Promise<void> {
  await client.rpc('move_pin_down', { p_graph_id: graphId })
}

export async function createCollection(
  client: ApiClient,
  userId: string,
  name: string,
  isDashboard: boolean,
): Promise<CaseCollection> {
  const created = await client.post<CaseCollection[]>(
    '/case_collection',
    { user_id: userId, name, is_dashboard: isDashboard },
    { headers: { Prefer: 'return=representation' } },
  )
  if (created.length === 0) throw new Error('createCollection: empty response')
  return caseCollectionSchema.parse(created[0])
}
